import type { ReactNode } from 'react';
import { useState } from 'react';
import { DeleteOutlined } from '@mui/icons-material';
import { Box, Button, FormHelperText, IconButton, MenuItem, Select, TextField, Typography } from '@mui/material';
import { CompanyAlias } from 'api';
import { useCompanyAliases, useCompanyAliasMutations } from 'services/hooks/companyAliases/useCompanyAliases';
import type { CompanyAliasWrite } from 'services/hooks/companyAliases/useCompanyAliases';
import extractResponseErrorMessage from 'utilities/extractResponseErrorMessage/extractResponseErrorMessage';

const KIND_LABELS: Record<CompanyAlias.kind, string> = {
  [CompanyAlias.kind.FORMER_NAME]: 'Former name',
  [CompanyAlias.kind.ACQUIRED]: 'Acquired company',
};
const EMPTY_DRAFT: CompanyAliasWrite = { kind: CompanyAlias.kind.FORMER_NAME, name: '' };
const NEW_ROW_KEY = 'new';
const DEFAULT_ERROR_MESSAGE = 'This name could not be saved. Try again.';
const ACTION_SLOT_WIDTH = 64;

interface KindSelectProps {
  disabled?: boolean;
  label: string;
  onChange: (kind: CompanyAlias.kind) => void;
  value: CompanyAlias.kind;
}

const KindSelect = ({ disabled, label, onChange, value }: KindSelectProps) => (
  <Select
    variant="standard"
    disabled={disabled}
    value={value}
    onChange={event => onChange(event.target.value as CompanyAlias.kind)}
    inputProps={{ 'aria-label': label }}
    sx={{ flexShrink: 0, width: 170 }}>
    {Object.values(CompanyAlias.kind).map(kind => (
      <MenuItem key={kind} value={kind}>
        {KIND_LABELS[kind]}
      </MenuItem>
    ))}
  </Select>
);

interface OtherNameRowProps {
  action: ReactNode;
  children: ReactNode;
  error?: string;
}

// Every row gives its action the same width so the kind selects line up in one column.
const OtherNameRow = ({ action, children, error }: OtherNameRowProps) => (
  <Box mb={1}>
    <Box display="flex" alignItems="flex-end" gap={1}>
      {children}
      <Box display="flex" justifyContent="center" flexShrink={0} width={ACTION_SLOT_WIDTH}>
        {action}
      </Box>
    </Box>
    {error && <FormHelperText error>{error}</FormHelperText>}
  </Box>
);

// Former names and acquired companies of a saved company, edited in place.
const OtherNames = ({ companyId }: { companyId: number }) => {
  const { data: aliases = [] } = useCompanyAliases(companyId);
  const { createAlias, removeAlias, updateAlias } = useCompanyAliasMutations(companyId);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savingRows, setSavingRows] = useState<Record<string, boolean>>({});

  // A row's error goes away as soon as that row is edited or saved again.
  const clearError = (rowKey: string) => setErrors(({ [rowKey]: _cleared, ...rest }) => rest);

  // Runs one row's save and locks that row until the save and refresh land, so a second edit cannot
  // race the first; a rejection shows the server's reason under that row.
  const saveRow = async (rowKey: string, save: () => Promise<unknown>) => {
    clearError(rowKey);
    setSavingRows(current => ({ ...current, [rowKey]: true }));
    try {
      await save();
      return true;
    } catch (error) {
      setErrors(current => ({ ...current, [rowKey]: extractResponseErrorMessage(error) ?? DEFAULT_ERROR_MESSAGE }));
      return false;
    } finally {
      setSavingRows(({ [rowKey]: _saved, ...rest }) => rest);
    }
  };

  const handleAdd = async () => {
    if (await saveRow(NEW_ROW_KEY, () => createAlias.mutateAsync({ ...draft, name: draft.name.trim() }))) {
      setDraft(EMPTY_DRAFT);
    }
  };

  return (
    <Box mb={2}>
      <Typography variant="subtitle2">Other names</Typography>
      <Typography variant="caption" color="text.secondary" component="p" mb={1}>
        Former names and acquired companies. Changes here save right away.
      </Typography>
      {aliases.map(alias => {
        const rowKey = String(alias.id);
        const isSaving = savingRows[rowKey];
        const update = (data: Partial<CompanyAliasWrite>) =>
          saveRow(rowKey, () => updateAlias.mutateAsync({ aliasId: alias.id, data }));

        return (
          <OtherNameRow
            key={alias.id}
            error={errors[rowKey]}
            action={
              <IconButton
                aria-label={`Remove ${alias.name}`}
                size="small"
                disabled={isSaving}
                onClick={() => saveRow(rowKey, () => removeAlias.mutateAsync(alias.id))}>
                <DeleteOutlined fontSize="small" />
              </IconButton>
            }>
            <TextField
              variant="standard"
              fullWidth
              disabled={isSaving}
              defaultValue={alias.name}
              inputProps={{ 'aria-label': 'Other name' }}
              onChange={() => clearError(rowKey)}
              onBlur={event => {
                const name = event.target.value.trim();
                if (name && name !== alias.name) {
                  update({ name });
                }
              }}
            />
            <KindSelect
              disabled={isSaving}
              label={`${alias.name} kind`}
              value={alias.kind}
              onChange={kind => update({ kind })}
            />
          </OtherNameRow>
        );
      })}
      <OtherNameRow
        error={errors[NEW_ROW_KEY]}
        action={
          <Button size="small" disabled={!draft.name.trim() || createAlias.isPending} onClick={handleAdd}>
            Add
          </Button>
        }>
        <TextField
          variant="standard"
          fullWidth
          placeholder="Add a former or acquired name"
          value={draft.name}
          inputProps={{ 'aria-label': 'New other name' }}
          onChange={event => {
            clearError(NEW_ROW_KEY);
            setDraft(current => ({ ...current, name: event.target.value }));
          }}
        />
        <KindSelect
          label="New other name kind"
          value={draft.kind}
          onChange={kind => setDraft(current => ({ ...current, kind }))}
        />
      </OtherNameRow>
    </Box>
  );
};

export default OtherNames;
